import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostBinding,
  signal,
  TemplateRef,
  input,
  contentChildren,
  inject,
  viewChild,
  viewChildren,
  output,
  effect
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { asapScheduler, timer } from 'rxjs';
import { V2TabDirective } from './tab/v2-tab.directive';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'coin-v2-tabs',
  templateUrl: './v2-tabs.component.html',
  styleUrls: ['./v2-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, MatTooltip, MatIcon, NgTemplateOutlet]
})
export class V2TabsComponent {
  private readonly destroyRef = inject(DestroyRef);

  public readonly type = input.required<TabStyle>();
  /** Automatically selects the first tab if no other tab is selected */
  public readonly defaultToFirstTab = input(true);
  public readonly tabSelected = output<V2TabDirective>();
  public readonly content = viewChild<TemplateRef<unknown>>('content');
  private readonly tabButtons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');

  public tabs = contentChildren(V2TabDirective);

  @HostBinding('class')
  private get className(): string {
    return `type-${this.type()}`;
  }

  private readonly activeTab = signal<V2TabDirective>(null);
  protected cachedTabs = new Set<V2TabDirective>();

  protected visibleTabs = computed(() => this.tabs().filter(tab => !tab.isHidden()));

  constructor() {
    this.setDefaultActiveTab();
  }

  public setSelectedTab(tab: V2TabDirective): void {
    this.activeTab.set(tab);

    if (tab) {
      if (tab.cache()) this.cachedTabs.add(tab);
      this.tabSelected.emit(tab);
    }
  }

  public isTabSelected(tab: V2TabDirective): boolean {
    if (!tab) return false;
    return tab === this.activeTab();
  }

  public selectTabByContent(content: string | TemplateRef<unknown>): void {
    const tab = this.visibleTabs().find(t => t.content() === content);
    if (tab) {
      this.setSelectedTab(tab);
    }
  }

  /** Simulate click to mark parent components for check. Required when tabs are used via @Viewchild. */
  private clickTab(index: number): void {
    timer(0, asapScheduler)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.tabButtons().at(index).nativeElement.click();
      });
  }

  private setDefaultActiveTab(): void {
    effect(() => {
      if (this.defaultToFirstTab() && !this.activeTab()) {
        this.clickTab(0);
      }
    });
  }
}

export const TAB_STYLES = ['primary', 'secondary'] as const;
export type TabStyle = (typeof TAB_STYLES)[number];
